import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { CookieBanner } from "@/components/cookies/CookieBanner";
import { CookiePreferencesDialog } from "@/components/cookies/CookiePreferencesDialog";
import { useDespia } from "@/hooks/useDespia";

const STORAGE_KEY = "cookie-consent";
const CURRENT_POLICY_VERSION = "2026-02-05";

export interface CookieCategories {
  analytics: boolean;
  personalization: boolean;
  marketing: boolean;
}

interface StoredConsent extends CookieCategories {
  policyVersion: string;
  timestamp: string;
}

interface CookieConsentContextValue {
  consent: CookieCategories | null;
  hasConsented: boolean;
  updateConsent: (categories: CookieCategories) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
  isPreferencesOpen: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function readStoredConsent(): CookieCategories | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: StoredConsent = JSON.parse(raw);
    if (!parsed.policyVersion) return null;
    return {
      analytics: parsed.analytics,
      personalization: parsed.personalization,
      marketing: parsed.marketing,
    };
  } catch {
    return null;
  }
}

function writeConsent(categories: CookieCategories) {
  const stored: StoredConsent = {
    ...categories,
    policyVersion: CURRENT_POLICY_VERSION,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieCategories | null>(() => readStoredConsent());
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const { isNative, trackingDisabled, isLoading: despiaLoading } = useDespia();

  const updateConsent = useCallback((categories: CookieCategories) => {
    writeConsent(categories);
    setConsent(categories);
  }, []);

  // When running inside Despia native and ATT tracking is disabled,
  // automatically reject all non-essential cookies for compliance.
  useEffect(() => {
    if (!isNative || despiaLoading || trackingDisabled === null) return;
    if (trackingDisabled && consent === null) {
      updateConsent({ analytics: false, personalization: false, marketing: false });
    }
  }, [isNative, despiaLoading, trackingDisabled, consent, updateConsent]);

  const acceptAll = useCallback(() => {
    updateConsent({ analytics: true, personalization: true, marketing: true });
  }, [updateConsent]);

  const rejectAll = useCallback(() => {
    updateConsent({ analytics: false, personalization: false, marketing: false });
  }, [updateConsent]);

  const openPreferences = useCallback(() => setIsPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setIsPreferencesOpen(false), []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasConsented: consent !== null,
        updateConsent,
        acceptAll,
        rejectAll,
        openPreferences,
        closePreferences,
        isPreferencesOpen,
      }}
    >
      {children}
      <CookieBanner />
      <CookiePreferencesDialog />
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}

/** Check if a specific cookie category has been accepted */
export function useConsentReady(category: keyof CookieCategories): boolean {
  const { consent } = useCookieConsent();
  return consent?.[category] ?? false;
}
